/**
 * CertificatesTab - Training certificates display and download
 * Extracted from TrainingView.jsx
 */

import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Award, Video, Truck, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CertificatesTab({ certificates }) {
  const [certSearch, setCertSearch] = useState('');

  const downloadCertificate = async (certificateId, driverName) => {
    try {
      const response = await axios.get(`${API}/training/certificates/${certificateId}/download`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `certificate_${driverName.replace(/\s+/g, '_')}_${certificateId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Certificate downloaded!');
    } catch (e) {
      toast.error('Failed to download certificate');
      console.error(e);
    }
  };

  // Defensive array check
  const safeCertificates = Array.isArray(certificates) ? certificates : [];
  
  const filteredCertificates = safeCertificates.filter(c =>
    c.driver_name?.toLowerCase().includes(certSearch.toLowerCase()) ||
    c.video_title?.toLowerCase().includes(certSearch.toLowerCase()) ||
    c.truck_number?.toLowerCase().includes(certSearch.toLowerCase()) ||
    c.certificate_id?.toLowerCase().includes(certSearch.toLowerCase())
  );

  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-border/30 overflow-hidden">
      <div className="p-4 border-b border-border/30">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Award className="w-6 h-6 text-yellow-500" />
            Training Certificates
          </h3>
          <div className="text-sm text-muted-foreground">
            📌 Certificates never expire - always available for download
          </div>
        </div>
        <input
          type="text"
          placeholder="Search by driver name, video, truck #, or certificate ID..."
          value={certSearch}
          onChange={e => setCertSearch(e.target.value)}
          className="w-full px-4 py-3 rounded-xl bg-secondary border border-border focus:border-yellow-500 outline-none"
          data-testid="cert-search-input"
        />
      </div>
      
      <div className="divide-y divide-border/30">
        {filteredCertificates.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Award className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">No certificates yet</p>
            <p className="text-sm mt-2">Certificates are automatically generated when a driver completes 100% of a training video.</p>
          </div>
        ) : (
          filteredCertificates.map((cert) => (
            <div key={cert.certificate_id} className="p-4 hover:bg-accent/5 transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                  <Award className="w-8 h-8 text-yellow-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-lg">{cert.driver_name}</p>
                  <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                    <Video className="w-3 h-3" /> {cert.video_title}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    {cert.truck_number && (
                      <span className="flex items-center gap-1 text-orange-400">
                        <Truck className="w-3 h-3" /> {cert.truck_number}
                      </span>
                    )}
                    <span>
                      Completed: {cert.completed_at ? new Date(cert.completed_at).toLocaleDateString() : 'N/A'}
                    </span>
                    <span className="text-yellow-500/70">
                      ID: {cert.certificate_id}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => downloadCertificate(cert.certificate_id, cert.driver_name)}
                  className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black rounded-xl font-medium flex items-center gap-2 transition-colors"
                  data-testid={`download-cert-${cert.certificate_id}`}
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      
      {filteredCertificates.length > 0 && (
        <div className="p-4 bg-yellow-500/10 border-t border-yellow-500/30">
          <p className="text-sm text-yellow-600 text-center">
            ✅ {safeCertificates.length} certificate(s) stored permanently • Training records are maintained for compliance
          </p>
        </div>
      )}
    </div>
  );
}
